import { useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { SelectField, TextField } from "@/components/Field";
import { Loaded } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import { CONSENT_METHODS, type ConsentMethod, type Group, type RosterEntry } from "@/types";

/**
 * Parental consent, per group.
 *
 * Institution-mediated by design: the school holds the relationship with the
 * family and Spontar never contacts one. What is stored is the fact, the method
 * and who attested to it — never a parent's name or address, which is why the
 * note field says so.
 *
 * Missing consent is listed **by name**. "Three missing" is not something an
 * owner can act on; three names are.
 */
export function ConsentPage() {
  const t = useT();
  const groups = useCallable<{ groups: Group[] }>(CALLABLES.listGroups);

  return (
    <SectionShell titleKey="consent.title">
      <p className="max-w-2xl text-ink-muted">{t("consent.intro")}</p>

      <div className="mt-8 flex flex-col gap-8">
        <Loaded state={groups}>
          {(loaded) => {
            const needing = loaded.groups.filter(
              (group) => group.containsMinors && !group.archivedAt
            );
            if (needing.length === 0) {
              return <EmptyState titleKey="consent.noGroups" />;
            }
            return needing.map((group) => (
              <GroupConsent key={group.id} group={group} />
            ));
          }}
        </Loaded>
      </div>
    </SectionShell>
  );
}

interface Readiness {
  ready: boolean;
  consent: { required: boolean; missing: RosterEntry[]; recorded: number };
}

function GroupConsent({ group }: { group: Group }) {
  const t = useT();
  const readiness = useCallable<Readiness, { groupId: string }>(CALLABLES.sessionReadiness, {
    groupId: group.id,
  });
  const [recording, setRecording] = useState<RosterEntry | undefined>(undefined);

  return (
    // A named region, so a screen reader can move between groups and a test
    // can scope an assertion to one of them.
    <section className="card p-5" aria-label={group.name}>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="section-title">{group.name}</h2>
        <Loaded state={readiness}>
          {(loaded) =>
            !loaded.consent.required ? (
              <span className="text-sm text-ink-muted">{t("consent.notRequired")}</span>
            ) : loaded.consent.missing.length === 0 ? (
              <span className="text-sm text-positive">{t("consent.group.complete")}</span>
            ) : (
              <span className="text-sm text-danger">
                {t("consent.group.missing", { count: loaded.consent.missing.length })}
              </span>
            )
          }
        </Loaded>
      </div>

      <div className="mt-4">
        <Loaded state={readiness}>
          {(loaded) => (
            <>
              {loaded.consent.missing.length > 0 ? (
                <>
                  <p className="text-sm font-medium text-ink-soft">
                    {t("consent.missingHeading")}
                  </p>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {loaded.consent.missing.map((entry) => (
                      <li
                        key={entry.uid}
                        className="flex items-center justify-between gap-4 text-sm"
                      >
                        <span className="text-ink">{entry.displayName}</span>
                        <Button variant="secondary" onClick={() => setRecording(entry)}>
                          {t("consent.record")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              <p className="mt-4 text-sm text-ink-muted">
                {t("consent.group.recorded", { recorded: loaded.consent.recorded })}
              </p>
            </>
          )}
        </Loaded>
      </div>

      <Modal
        open={recording !== undefined}
        onClose={() => setRecording(undefined)}
        titleKey="consent.record"
      >
        {recording ? (
          <RecordConsentForm
            group={group}
            entry={recording}
            onDone={() => {
              setRecording(undefined);
              readiness.reload();
            }}
          />
        ) : null}
      </Modal>
    </section>
  );
}

function RecordConsentForm({
  group,
  entry,
  onDone,
}: {
  group: Group;
  entry: RosterEntry;
  onDone: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();
  const [method, setMethod] = useState<ConsentMethod>("paper_form");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await call(CALLABLES.recordParentalConsent, {
        groupId: group.id,
        uid: entry.uid,
        method,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      success("consent.recorded", { name: entry.displayName });
      onDone();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink">
        {entry.displayName}
        <span className="ml-2 text-ink-muted">{formatDate(locale, entry.addedAt)}</span>
      </p>

      <SelectField
        labelKey="consent.method"
        value={method}
        onChange={(event) => setMethod(event.target.value as ConsentMethod)}
        options={CONSENT_METHODS.map((value) => ({
          value,
          labelKey: `consent.method.${value}`,
        }))}
      />

      <TextField
        labelKey="consent.note"
        hintKey="consent.noteHint"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button busy={busy} onClick={submit}>
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}
